import {
  DataFeedFileType,
  Dataset,
} from '@cbinsights/externalapiservice/externalapiservice';
import { DefaultOptionType } from 'antd/lib/select';
import { Moment } from 'moment';

export type JSONRequest = {
  payload: string;
  sourceName: string;
  size: number;
};

type DataFeedDestination = {
  type: DefaultOptionType;
  credentials: string;
  path: string;
  keepOnlyLatest: boolean;
};

export const DataFeedDestinationFormField = {
  type: 'type',
  keepOnlyLatest: 'keepOnlyLatest',
} as const;

export type FileType = {
  name: string;
  excludedColumns: string[];
};

export type SearchConfig = {
  uuid: string;
  tab: DefaultOptionType;
  topNOrgs: string;
};

export const DataFeedFormField = {
  dataset: 'dataset',
  files: 'files',
  optionalColumns: 'optionalColumns',
  filters: 'filters',
  destination: 'destination',
  frequency: 'frequency',
  dataFeedName: 'dataFeedName',
  client: 'client',
  deltas: 'deltas',
};

export type SupportedDataset =
  | Dataset.FIRMOGRAPHICS
  | Dataset.BUSINESS_RELATIONSHIPS
  | Dataset.FINANCIAL_TRANSACTIONS
  | Dataset.MANAGEMENT_AND_BOARD
  | Dataset.OUTLOOK
  | Dataset.REVENUE;

export type DataFeedFieldTypes = {
  dataset: SupportedDataset;
  files: DataFeedFileType[];
  optionalColumns: Partial<Record<DataFeedFileType, string[]>>;
  filters: Partial<{
    [Dataset.FIRMOGRAPHICS]: Partial<DataFeedFirmographicsTypes>;
    [Dataset.BUSINESS_RELATIONSHIPS]: Partial<DataFeedFirmographicsTypes>;
    [Dataset.FINANCIAL_TRANSACTIONS]: Partial<DataFeedFirmographicsTypes>;
    [Dataset.MANAGEMENT_AND_BOARD]: Partial<DataFeedFirmographicsTypes>;
    [Dataset.OUTLOOK]: Partial<DataFeedFirmographicsTypes>;
    [Dataset.REVENUE]: Partial<DataFeedFirmographicsTypes>;
    searchConfig: SearchConfig;
    searchUrl: string;
    topNOrgs: number;
    jsonRequest: JSONRequest;
  }>;
  destination: DataFeedDestination;
  frequency: DefaultOptionType;
  dataFeedName: string;
  client: DefaultOptionType;
  deltas: boolean;
};

export type DataFeedForm = {
  [K in keyof typeof DataFeedFormField]: DataFeedFieldTypes[K];
};

export const DatasetLabels: Record<SupportedDataset, string> = {
  [Dataset.FIRMOGRAPHICS]: 'Firmographics',
  [Dataset.BUSINESS_RELATIONSHIPS]: 'Business relationships',
  [Dataset.FINANCIAL_TRANSACTIONS]: 'Financial transactions',
  [Dataset.MANAGEMENT_AND_BOARD]: 'Management and board',
  [Dataset.OUTLOOK]: 'Outlook',
  [Dataset.REVENUE]: 'Revenue',
};

export const isSupportedDataset = (
  dataset: Dataset
): dataset is SupportedDataset => dataset in DatasetLabels;

export const FileTypeMappingByDataset = {
  [Dataset.FIRMOGRAPHICS]: [
    DataFeedFileType.FILE_TYPE_FIRMOGRAPHICS,
    DataFeedFileType.FILE_TYPE_RELATED_ORGS,
    DataFeedFileType.FILE_TYPE_COMPETITORS,
    DataFeedFileType.FILE_TYPE_FIRMOGRAPHICS_LANDSCAPE,
    DataFeedFileType.FILE_TYPE_FIRMOGRAPHICS_NAICS_TAXONOMY,
  ],
  [Dataset.BUSINESS_RELATIONSHIPS]: [
    DataFeedFileType.FILE_TYPE_BUSINESS_RELATIONSHIPS,
    DataFeedFileType.FILE_TYPE_BUSINESS_RELATIONSHIP_PARTNERS,
    DataFeedFileType.FILE_TYPE_BUSINESS_RELATIONSHIP_SOURCES,
  ],
  [Dataset.FINANCIAL_TRANSACTIONS]: [
    DataFeedFileType.FILE_TYPE_FINANCIAL_TRANSACTIONS,
    DataFeedFileType.FILE_TYPE_FINANCIAL_TRANSACTION_INVESTORS,
    DataFeedFileType.FILE_TYPE_FINANCIAL_TRANSACTIONS_CAP_TABLE_HISTORY,
    DataFeedFileType.FILE_TYPE_FINANCIAL_TRANSACTIONS_PORTFOLIO_EXITS,
    DataFeedFileType.FILE_TYPE_FINANCIAL_TRANSACTIONS_MNA,
    DataFeedFileType.FILE_TYPE_FINANCIAL_TRANSACTION_INVESTORS_MNA,
    DataFeedFileType.FILE_TYPE_FINANCIAL_TRANSACTIONS_CAP_TABLE_HISTORY_MNA,
    DataFeedFileType.FILE_TYPE_FINANCIAL_TRANSACTIONS_PORTFOLIO_EXITS_MNA,
  ],
  [Dataset.MANAGEMENT_AND_BOARD]: [
    DataFeedFileType.FILE_TYPE_MANAGEMENT_AND_BOARD,
    DataFeedFileType.FILE_TYPE_MANAGEMENT_AND_BOARD_EDUCATION,
    DataFeedFileType.FILE_TYPE_MANAGEMENT_AND_BOARD_EXPERIENCE,
    DataFeedFileType.FILE_TYPE_MANAGEMENT_AND_BOARD_BOARD_ASSOCIATIONS,
    DataFeedFileType.FILE_TYPE_MANAGEMENT_AND_BOARD_LEADERSHIP_SIGNALS,
  ],
  [Dataset.OUTLOOK]: [
    DataFeedFileType.FILE_TYPE_COMPANY_OUTLOOK,
    DataFeedFileType.FILE_TYPE_MOSAIC_SCORE_HISTORY,
    DataFeedFileType.FILE_TYPE_EXIT_PROBABILITY_HISTORY,
    DataFeedFileType.FILE_TYPE_COMMERCIAL_MATURITY_HISTORY,
    DataFeedFileType.FILE_TYPE_COMPANY_OUTLOOK_MOSAIC_2_1,
    DataFeedFileType.FILE_TYPE_OUTLOOK_FUNDING_WINDOW,
    DataFeedFileType.FILE_TYPE_OUTLOOK_FUNDING_WINDOW_COHORT_CRITERIA,
  ],
  [Dataset.REVENUE]: [
    DataFeedFileType.FILE_TYPE_REVENUE_BY_YEAR,
    DataFeedFileType.FILE_TYPE_REVENUE_BY_YEAR_SOURCES,
  ],
} as const satisfies Record<SupportedDataset, readonly DataFeedFileType[]>;

// DataFeedFileType numbers are contiguous from 0 in proto declaration order; UNRECOGNIZED is appended last.
const fileTypeOrder = Object.values(DataFeedFileType);
export const fileTypeToNumber = (ft: DataFeedFileType) =>
  fileTypeOrder.indexOf(ft);

export const DataFeedFirmographicsFilters = {
  orgIds: 'orgIds',
  orgNames: 'orgNames',
  urls: 'urls',
  companyStatus: 'companyStatus',
  descriptions: 'descriptions',
  entityType: 'entityType',
  tier1Only: 'tier1Only',
  sectors: 'sectors',
  industries: 'industries',
  subIndustries: 'subIndustries',
  collectionIds: 'collectionIds',
  investorTypeIds: 'investorTypeIds',
  vcBacked: 'vcBacked',
  maxTotalFundingInMillions: 'maxTotalFundingInMillions',
  minTotalFundingInMillions: 'minTotalFundingInMillions',
  lastFundingRoundCategoryIds: 'lastFundingRoundCategoryIds',
  minDealDate: 'minDealDate',
  maxDealDate: 'maxDealDate',
  continentIds: 'continentIds',
  regionIds: 'regionIds',
  countries: 'countries',
  states: 'states',
  cities: 'cities',
  technologyIds: 'technologyIds',
  businessModelIds: 'businessModelIds',
  awardsAndSpotlightsIds: 'awardsAndSpotlightsIds',
};

type DataFeedFirmographicsTypes = {
  orgIds: DefaultOptionType[];
  orgNames: DefaultOptionType[];
  urls: string[];
  companyStatus: DefaultOptionType[];
  descriptions: string[];
  entityType: DefaultOptionType;
  tier1Only: boolean;
  sectors: DefaultOptionType[];
  industries: DefaultOptionType[];
  subIndustries: DefaultOptionType[];
  collectionIds: DefaultOptionType[];
  vcBacked: DefaultOptionType;
  maxTotalFundingInMillions: number;
  minTotalFundingInMillions: number;
  lastFundingRoundCategoryIds: DefaultOptionType[];
  minDealDate: Moment;
  maxDealDate: Moment;
  continentIds: DefaultOptionType[];
  regionIds: DefaultOptionType[];
  countries: DefaultOptionType[];
  states: {
    [key: number]: DefaultOptionType[];
  };
  cities: {
    [key: number]: {
      [key: number]: DefaultOptionType[];
    };
  };
  investorTypeIds: DefaultOptionType[];
  technologyIds: DefaultOptionType[];
  businessModelIds: DefaultOptionType[];
  awardsAndSpotlightsIds: DefaultOptionType[];
};

export type DataFeedFirmographicsForm = {
  [K in keyof typeof DataFeedFirmographicsFilters]: DataFeedFirmographicsTypes[K];
};
